import { useCallback, useEffect, useState } from 'react';
import { Switch } from '@mui/material';
import { useSnackbar } from 'notistack';
import PermissionGate from '../../admin/components/PermissionGate';
import { fetchAdmin, putAdmin, toErrorMessage } from '../../admin/api';
import { AdminDataTable } from '../../sections/admin/shared';
import { AdminRouteLayout, RequestState, useAsyncState } from './AdminRoutePageShared';

export default function ConfigFeatureFlagsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const flags = useAsyncState([]);
  const { state: flagsState, setLoading: setFlagsLoading, setData: setFlagsData, setError: setFlagsError } = flags;
  const [updatingKey, setUpdatingKey] = useState('');

  const loadFlags = useCallback(async () => {
    setFlagsLoading();
    try {
      const response = await fetchAdmin('/config/feature_flags');
      setFlagsData(response.flags || []);
    } catch (error) {
      setFlagsError(toErrorMessage(error));
    }
  }, [setFlagsData, setFlagsError, setFlagsLoading]);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const updateFlag = async (flag) => {
    setUpdatingKey(flag.flag_key);
    try {
      await putAdmin(`/config/feature_flags/${flag.flag_key}`, {
        is_enabled: !Boolean(Number(flag.is_enabled)),
      });
      enqueueSnackbar('Feature flag updated', { variant: 'success' });
      await loadFlags();
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setUpdatingKey('');
    }
  };

  return (
    <AdminRouteLayout title="Feature Flags" breadcrumbTitle="Config">
      <RequestState loading={flagsState.loading} error={flagsState.error}>
        <AdminDataTable
          title="Feature Flags"
          description="Dedicated feature-flag management backed by `admin_feature_flags`. Toggle actions send real JSON booleans only and re-fetch the dedicated flags endpoint."
          columns={[
            { field: 'label', headerName: 'Label' },
            { field: 'flagKey', headerName: 'Flag Key' },
            { field: 'description', headerName: 'Description' },
            { field: 'scope', headerName: 'Scope' },
            { field: 'updatedAt', headerName: 'Updated At' },
            {
              field: 'enabled',
              headerName: 'Enabled',
              render: (row) => (
                <PermissionGate moduleName="config" permissionCode="config.manage">
                  <Switch
                    checked={row.enabled}
                    onChange={() => updateFlag(row.original)}
                    disabled={updatingKey === row.flagKey}
                  />
                </PermissionGate>
              ),
            },
          ]}
          rows={(flagsState.data || []).map((flag) => ({
            id: flag.id,
            original: flag,
            label: flag.label,
            flagKey: flag.flag_key,
            description: flag.description || 'No description returned',
            scope: flag.scope,
            updatedAt: flag.updated_at || 'Not updated',
            enabled: Boolean(Number(flag.is_enabled)),
          }))}
          emptyTitle="No feature flags"
          emptyDescription="The dedicated feature flags endpoint returned no rows."
        />
      </RequestState>
    </AdminRouteLayout>
  );
}
